import { useEffect, useState } from "react";
import { apiRequest } from "../services/apiClient";
import { collection, getDocs, db } from "../services/mysqlStore";
import { fetchProducts } from "../services/sinaliteService";

function ProductSearchSelect({ products, onPick }) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const matches = products.filter(p => `${p.name} ${p.category || ""}`.toLowerCase().includes(term.trim().toLowerCase())).slice(0, 100);
  return <div style={{ position: "relative", marginBottom: 6 }} onBlur={() => window.setTimeout(() => setOpen(false), 150)}>
    <input value={term} placeholder={products.length ? `Search ${products.length} products…` : "Loading products…"}
      onFocus={() => setOpen(true)} onChange={event => { setTerm(event.target.value); setOpen(true); }} />
    {open && <div style={{ position: "absolute", zIndex: 50, left: 0, right: 0, top: "100%", maxHeight: 260, overflowY: "auto", background: "var(--adm-card-bg, #1b1b2f)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6 }}>
      {matches.length === 0 && <div style={{ padding: 10, opacity: 0.7 }}>No products found</div>}
      {matches.map(p => <div key={p.id} role="option" style={{ padding: "8px 10px", cursor: "pointer", color: "var(--adm-text)" }}
        onMouseDown={event => { event.preventDefault(); onPick(p); setTerm(""); setOpen(false); }}>
        {p.name}{p.category ? <small style={{ opacity: 0.6 }}> — {p.category}</small> : null}
      </div>)}
    </div>}
  </div>;
}

const emptyItem = { description: "", quantity: 1, unitPrice: 0 };
const emptyForm = () => ({ title: "", customerName: "", customerEmail: "", customerAddress: "", shipping: 0, hstRate: 13, notes: "", items: [{ ...emptyItem }] });

export default function AdminQuotations() {
  const [quotations, setQuotations] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  async function load() {
    try { setQuotations(await apiRequest("/admin/quotations")); }
    catch (error) { setMessage(error.message); }
  }
  useEffect(() => { load(); }, []);
  const [products, setProducts] = useState([]);
  useEffect(() => {
    Promise.all([
      fetchProducts().then(result => result.allProducts).catch(() => []),
      getDocs(collection(db, "products")).then(snap => snap.docs.map(d => ({ id: `p-${d.id}`, ...d.data() }))).catch(() => []),
    ]).then(([catalog, local]) => {
      const seen = new Set();
      setProducts([...local, ...catalog].filter(p => p.name && !seen.has(p.name) && seen.add(p.name))
        .sort((a, b) => String(a.name).localeCompare(String(b.name))));
    });
  }, []);

  function pickProduct(index, product) {
    const firstTier = (product.quantityPrices || []).find(q => q.quantity && q.price);
    const quantity = firstTier ? Number(firstTier.quantity) : 1;
    const price = firstTier ? Number(firstTier.price) / (quantity || 1) : Number(product.startingPrice || product.price || 0);
    setForm(previous => ({ ...previous, items: previous.items.map((item, i) => i === index ? { ...item, description: product.name, quantity, unitPrice: Number(price.toFixed(4)) } : item) }));
  }

  const subtotal = form.items.reduce((sum, item) => sum + Number(item.quantity || 0) * Number(item.unitPrice || 0), 0);
  const hst = (subtotal + Number(form.shipping || 0)) * Number(form.hstRate || 0) / 100;
  const total = subtotal + Number(form.shipping || 0) + hst;

  function updateItem(index, key, value) {
    setForm(previous => ({ ...previous, items: previous.items.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item) }));
  }

  async function saveQuotation(emailCustomer = false) {
    setSaving(true);
    setMessage("");
    try {
      const quotation = await apiRequest(editingId ? `/admin/quotations/${editingId}` : "/admin/quotations", {
        method: editingId ? "PUT" : "POST",
        body: JSON.stringify(form),
      });
      if (emailCustomer) {
        try {
          const result = await apiRequest(`/admin/quotations/${quotation.id}/email`, { method: "POST", body: JSON.stringify({}) });
          setMessage(result.message);
        } catch (error) {
          setMessage(`Error: Quotation ${quotation.quotationNumber} saved, but email failed: ${error.message}`);
        }
      } else setMessage(`Quotation ${quotation.quotationNumber} saved.`);
      setForm(emptyForm());
      setEditingId(null);
      await load();
    } catch (error) { setMessage(`Error: ${error.message}`); }
    finally { setSaving(false); }
  }

  function editQuotation(quotation) {
    setEditingId(quotation.id);
    setForm({ ...emptyForm(), ...quotation, items: quotation.items?.length ? quotation.items : [{ ...emptyItem }] });
    setMessage("");
  }

  async function deleteQuotation(quotation) {
    if (!window.confirm(`Delete quotation ${quotation.quotationNumber}?`)) return;
    try {
      await apiRequest(`/admin/quotations/${quotation.id}`, { method: "DELETE" });
      if (editingId === quotation.id) { setEditingId(null); setForm(emptyForm()); }
      setMessage(`Quotation ${quotation.quotationNumber} deleted.`);
      await load();
    } catch (error) { setMessage(error.message); }
  }

  async function emailQuotation(quotation) {
    setSendingId(quotation.id);
    setMessage("");
    try {
      const result = await apiRequest(`/admin/quotations/${quotation.id}/email`, { method: "POST", body: JSON.stringify({}) });
      setMessage(result.message);
    } catch (error) { setMessage(`Error: ${error.message}`); }
    finally { setSendingId(null); }
  }

  return <div style={{ display: "grid", gap: 18 }}>
    <section className="adm-card" style={{ padding: 22 }}>
      <h2 style={{ color: "var(--adm-text)", marginTop: 0 }}>{editingId ? "Edit Quotation" : "Create Quotation"}</h2>
      <div className="adm-form">
        <div className="adm-form-group"><label>Quotation name</label><input value={form.title} onChange={event => setForm({ ...form, title: event.target.value })} placeholder="e.g. 500 business cards" /></div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
          <div className="adm-form-group"><label>Customer name</label><input value={form.customerName} onChange={event => setForm({ ...form, customerName: event.target.value })} /></div>
          <div className="adm-form-group"><label>Customer email</label><input type="email" value={form.customerEmail} onChange={event => setForm({ ...form, customerEmail: event.target.value })} /></div>
        </div>
        <div className="adm-form-group"><label>Customer address</label><textarea rows={2} value={form.customerAddress} onChange={event => setForm({ ...form, customerAddress: event.target.value })} /></div>
        <h3 style={{ color: "var(--adm-text)", marginBottom: 0 }}>Items</h3>
        {form.items.map((item, index) => <div key={index} style={{ display: "grid", gridTemplateColumns: "minmax(140px, 1fr) 80px 110px auto", gap: 8, alignItems: "end" }}>
          <div className="adm-form-group"><label>Description</label>
            <ProductSearchSelect products={products} onPick={product => pickProduct(index, product)} />
            <input value={item.description} placeholder="…or type manually" onChange={event => updateItem(index, "description", event.target.value)} /></div>
          <div className="adm-form-group"><label>Qty</label><input type="number" min="1" value={item.quantity} onChange={event => updateItem(index, "quantity", event.target.value)} /></div>
          <div className="adm-form-group"><label>Unit price</label><input type="number" min="0" step="0.01" value={item.unitPrice} onChange={event => updateItem(index, "unitPrice", event.target.value)} /></div>
          <button type="button" className="adm-btn adm-btn-danger" aria-label="Remove item" onClick={() => setForm({ ...form, items: form.items.filter((_, itemIndex) => itemIndex !== index) })}>×</button>
        </div>)}
        <button type="button" className="adm-btn" onClick={() => setForm({ ...form, items: [...form.items, { ...emptyItem }] })}>+ Add item</button>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
          <div className="adm-form-group"><label>Shipping</label><input type="number" min="0" step="0.01" value={form.shipping} onChange={event => setForm({ ...form, shipping: event.target.value })} /></div>
          <div className="adm-form-group"><label>HST %</label><input type="number" min="0" step="0.01" value={form.hstRate} onChange={event => setForm({ ...form, hstRate: event.target.value })} /></div>
        </div>
        <div className="adm-form-group"><label>Notes</label><textarea rows={3} value={form.notes} onChange={event => setForm({ ...form, notes: event.target.value })} /></div>
        <div style={{ color: "var(--adm-text)", lineHeight: 1.7 }}>Shipping: ${Number(form.shipping || 0).toFixed(2)}<br />Subtotal: ${(subtotal + Number(form.shipping || 0)).toFixed(2)}<br />HST ({Number(form.hstRate || 0).toFixed(2)}%): ${hst.toFixed(2)}<br /><strong>Grand Total: ${total.toFixed(2)} CAD</strong></div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="adm-btn adm-btn-primary" disabled={saving || !form.title.trim()} onClick={() => saveQuotation(false)}>{saving ? "Saving..." : editingId ? "Update quotation" : "Save quotation"}</button>
          <button className="adm-btn" disabled={saving || !form.title.trim() || !form.customerEmail.trim()} onClick={() => saveQuotation(true)}>{saving ? "Saving..." : "Save &amp; email customer"}</button>
          {editingId && <button className="adm-btn" type="button" onClick={() => { setEditingId(null); setForm(emptyForm()); }}>Cancel</button>}
        </div>
        {message && <p role="status" style={{ color: message.startsWith("Error:") ? "#f87171" : "#4ade80" }}>{message}</p>}
      </div>
    </section>
    <section className="adm-card">
      <div className="adm-card-header"><span className="adm-card-title">Saved Quotations ({quotations.length})</span></div>
      <div className="adm-table-wrap"><table className="adm-table"><thead><tr><th>Quotation</th><th>Customer</th><th>Total</th><th>Date</th><th></th></tr></thead><tbody>
        {quotations.map(quotation => {
          const quotationSubtotal = (quotation.items || []).reduce((sum, item) => sum + Number(item.quantity || 0) * Number(item.unitPrice || 0), 0);
          const quotationTotal = quotationSubtotal + Number(quotation.shipping || 0) + (quotationSubtotal + Number(quotation.shipping || 0)) * Number(quotation.hstRate ?? 13) / 100;
          return <tr key={quotation.id}><td>{quotation.quotationNumber}<br /><span style={{ color: "var(--adm-muted)" }}>{quotation.title}</span></td><td>{quotation.customerName || quotation.customerEmail || "—"}</td><td>${quotationTotal.toFixed(2)} CAD</td><td>{quotation.createdAt ? new Date(quotation.createdAt).toLocaleDateString() : "—"}</td><td style={{ display: "flex", gap: 6 }}><button className="adm-btn adm-btn-sm" onClick={() => editQuotation(quotation)}>Edit</button><button className="adm-btn adm-btn-sm" disabled={!quotation.customerEmail || sendingId === quotation.id} title={quotation.customerEmail ? "Email this quotation" : "Add a customer email before sending"} onClick={() => emailQuotation(quotation)}>{sendingId === quotation.id ? "Sending..." : "Email"}</button><button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => deleteQuotation(quotation)}>Delete</button></td></tr>;
        })}
        {!quotations.length && <tr><td colSpan="5" style={{ color: "var(--adm-muted)" }}>No quotations yet.</td></tr>}
      </tbody></table></div>
    </section>
  </div>;
}